import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import {
    clearFocusedAsset,
    clearTextAssetTrackedSlide,
    clearTrackedSlide,
    clearTrackedSlideTextAssets,
    endPasteUpload,
    hideAllSceneSections,
    hideFullscreenSelector,
    hideFullscreenSwapSceneSelector,
    hideOverlaySelector,
    setFocusedAssetInfo,
    setFocusedSceneInfo,
    setMediaTransformEditState,
    setOverlayForSceneGroup,
    setSceneSelectorForSlide,
    setSwapSceneDesignForSlide,
    showFullscreenSelector,
    showFullscreenSwapSceneSelector as action_showFullscreenSwapSceneSelector,
    showOverlaySelector,
    startPasteUpload,
    trackChangedSceneDesign,
    trackChangedTextAsset,
    trackChangedTextAssetSlide,
} from 'js/src/data/scenes/scenes-actions';
import type {
    MediaTransformEditState,
    OverlayBounds,
    PasteUploadState,
} from 'js/src/data/scenes/scenes-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

/**
 * Contains all slides that have a scene design applied that is different from
 * the default scene design (i.e. a new design was chosen from the scene selector)
 */
export function sceneDesignChangedSet(state = Immutable.Set<string>(), action: AnyAction) {
    if (trackChangedSceneDesign.match(action)) {
        return state.add(action.payload.sceneId);
    }
    if (clearTrackedSlide.match(action)) {
        return state.delete(action.payload.sceneId);
    }
    return state;
}

export function sceneDesignTextAssetChangedSet(
    state = Immutable.Map<string, Immutable.List<string>>(),
    action: AnyAction,
) {
    if (trackChangedTextAssetSlide.match(action)) {
        return state.set(action.payload.sceneId, Immutable.List([]));
    }
    if (trackChangedTextAsset.match(action)) {
        const list = state.get(action.payload.sceneId);
        let updatedSceneAssetList = Immutable.List([action.payload.assetId]);
        if (list) {
            updatedSceneAssetList = list.push(action.payload.assetId);
        }
        return state.set(action.payload.sceneId, updatedSceneAssetList);
    }
    if (clearTextAssetTrackedSlide.match(action)) {
        return state.delete(action.payload.sceneId);
    }
    if (clearTrackedSlideTextAssets.match(action)) {
        const list = state.get(action.payload.sceneId);
        if (list) {
            const clearedSceneAssetList = list.clear();
            return state.set(action.payload.sceneId, clearedSceneAssetList);
        }
        return state;
    }
    return state;
}

type FullscreenSceneSelectorState = {
    visible: boolean;
    targetSlideIndex: number | null;
    targetSlideId: string | null;
    targetSlideGroupId: string | null;
    sceneGroupCategory: string | null;
};
export function showFullscreenSceneSelector(
    state: ImmutableMap<FullscreenSceneSelectorState> = ImmutableFromJS({
        visible: false,
        targetSlideIndex: null,
        targetSlideId: null,
        targetSlideGroupId: null,
        sceneGroupCategory: null,
    }),
    action: AnyAction,
) {
    if (showFullscreenSelector.match(action)) {
        return state.merge({
            visible: true,
            targetSlideIndex: action.payload.targetSlideIndex,
            targetSlideId: action.payload.targetSlideId,
            targetSlideGroupId: action.payload.targetSlideGroupId,
            sceneGroupCategory: action.payload.sceneGroupCategory,
        }) as unknown as ImmutableMap<FullscreenSceneSelectorState>;
    }
    if (hideFullscreenSelector.match(action)) {
        return state.merge({
            visible: false,
        }) as unknown as ImmutableMap<FullscreenSceneSelectorState>;
    }
    if (hideAllSceneSections.match(action)) {
        return state.merge({
            visible: false,
        }) as unknown as ImmutableMap<FullscreenSceneSelectorState>;
    }
    return state;
}

export function showFullscreenSwapSceneSceneSelector(state = false, action: AnyAction) {
    if (action_showFullscreenSwapSceneSelector.match(action)) {
        return true;
    }
    if (hideFullscreenSwapSceneSelector.match(action)) {
        return false;
    }
    if (hideAllSceneSections.match(action)) {
        return false;
    }
    return state;
}

export function overlaySelectorShowing(state = false, action: AnyAction) {
    if (showOverlaySelector.match(action)) {
        return true;
    }
    if (hideOverlaySelector.match(action)) {
        return false;
    }
    if (hideAllSceneSections.match(action)) {
        return false;
    }
    return state;
}

/**
 * The start and end times of the overlay selector, in video time.
 */
export function overlayBounds(
    state: ImmutableMap<OverlayBounds> = ImmutableFromJS({
        startTime: null,
        endTime: null,
    }),
    action: AnyAction,
) {
    if (showOverlaySelector.match(action)) {
        return state.merge({
            startTime: action.payload.startTime,
            endTime: action.payload.endTime,
        }) as unknown as ImmutableMap<OverlayBounds>;
    }
    if (hideOverlaySelector.match(action)) {
        return state.merge({
            startTime: null,
            endTime: null,
        }) as unknown as ImmutableMap<OverlayBounds>;
    }
    if (hideAllSceneSections.match(action)) {
        return state.merge({
            startTime: null,
            endTime: null,
        }) as unknown as ImmutableMap<OverlayBounds>;
    }
    return state;
}

export function overlaySceneGroupId(state: string | null = null, action: AnyAction) {
    if (showOverlaySelector.match(action)) {
        return action.payload.sceneGroupId;
    }
    if (hideOverlaySelector.match(action)) {
        return null;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

/**
 * Determine whether the sidebar will render the scene selector, and for
 * which slide it will show scene designs for.
 */
export function sceneSelectorSlideId(state: string | null = null, action: AnyAction) {
    if (setSceneSelectorForSlide.match(action)) {
        return action.payload.sceneId;
    }
    if (setSwapSceneDesignForSlide.match(action)) {
        return action.payload.sceneId ? null : state;
    }
    if (setOverlayForSceneGroup.match(action)) {
        return action.payload.sceneGroupId ? null : state;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

/**
 * Determine whether the sidebar will render the swap scene design tab
 * and for which slide it will show scene designs for.
 */
export function swapSceneDesignSlideId(state: string | null = null, action: AnyAction) {
    if (setSwapSceneDesignForSlide.match(action)) {
        return action.payload.sceneId;
    }
    if (setSceneSelectorForSlide.match(action)) {
        return action.payload.sceneId ? null : state;
    }
    if (setOverlayForSceneGroup.match(action)) {
        return action.payload.sceneGroupId ? null : state;
    }
    if (hideFullscreenSwapSceneSelector.match(action)) {
        return null;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

type CurrentlyEditingAssetData = {
    sceneGroupId: string | null;
    sceneId: string | null;
    subsceneIndex: number | null;
    assetId: string | null;
    assetType: string | null;
    rootAssetGroupId?: string | null;
};

/**
 * Which scene group, scene, subscene, and asset are currently in focus. Assets being in focus means they're selected
 * (and are being edited). Scenes can be in focus without an asset being in focus.
 * Not all assets will have a root asset group.
 */
export function focusedSceneInfo(
    state: ImmutableMap<CurrentlyEditingAssetData> = Immutable.Map({
        sceneGroupId: null,
        sceneId: null,
        subsceneIndex: null,
        assetId: null,
        assetType: null,
        rootAssetGroupId: null,
    }) as unknown as ImmutableMap<CurrentlyEditingAssetData>,
    action: AnyAction,
) {
    if (setFocusedSceneInfo.match(action)) {
        return ImmutableFromJS({
            sceneGroupId: action.payload.sceneGroupId,
            sceneId: action.payload.sceneId,
            subsceneIndex: action.payload.subsceneIndex ?? 0,
            assetId: null,
            assetType: null,
            rootAssetGroupId: null,
        });
    }
    if (setFocusedAssetInfo.match(action)) {
        return ImmutableFromJS({
            sceneGroupId: action.payload.sceneGroupId,
            sceneId: action.payload.sceneId,
            subsceneIndex: action.payload.subsceneIndex,
            assetId: action.payload.assetId,
            assetType: action.payload.assetType,
            rootAssetGroupId: action.payload.rootAssetGroupId ?? null,
        });
    }
    if (clearFocusedAsset.match(action)) {
        // clear the asset info but keep the scene info
        return state.set('assetId', null).set('assetType', null).set('rootAssetGroupId', null);
    }
    return state;
}

export function pasteUploads(
    state: Immutable.Map<string, ImmutableMap<PasteUploadState>> = Immutable.Map(),
    action: AnyAction,
) {
    if (startPasteUpload.match(action)) {
        const pasteUploadState: PasteUploadState = {
            assetId: action.payload.assetId,
            sceneId: action.payload.sceneId,
            uploadSessionId: action.payload.uploadSessionId,
            status: 'uploading',
            thumbnailDataUrl: action.payload.thumbnailDataUrl,
        };
        return state.set(action.payload.assetId, ImmutableFromJS(pasteUploadState));
    }
    if (endPasteUpload.match(action)) {
        return state.delete(action.payload.assetId);
    }
    return state;
}

// Which asset (if any) has an in-scene media transform session open. Bound to the focused asset,
// so any focus change or clear ends the session.
export function mediaTransformEdit(
    state: MediaTransformEditState = null,
    action: AnyAction,
): MediaTransformEditState {
    if (setMediaTransformEditState.match(action)) {
        return action.payload;
    }
    if (
        setFocusedSceneInfo.match(action) ||
        setFocusedAssetInfo.match(action) ||
        clearFocusedAsset.match(action)
    ) {
        return null;
    }
    return state;
}

export const scenes = combineReducers({
    sceneDesignChangedSet,
    sceneSelectorSlideId,
    swapSceneDesignSlideId,
    showFullscreenSceneSelector,
    showFullscreenSwapSceneSceneSelector,
    sceneDesignTextAssetChangedSet,
    focusedSceneInfo,
    overlaySelectorShowing,
    overlayBounds,
    overlaySceneGroupId,
    pasteUploads,
    mediaTransformEdit,
});

export type ScenesState = ReturnType<typeof scenes>;
